import React, {
    useState, useRef, useEffect,
} from 'react';
import styled from 'styled-components';

import SELECTS_TEXTS from '@shared/i18n/inputs.texts';
import TEST_ID from '@shared/constants/testId.constants';
import { useToggle } from '@shared/utils/toggle/toggle.hooks';
import { SelectorInputWithMagnifier } from '../../text-fields/RoundedInput';
import DropDown from './components/DropDown';
import { MobileScreen } from '../../../styles/ConstantMediaQuery';
import SingleValueWithDropDown from './components/SingleValueWithDropDown';
import SubMenuWrapper from './components/sub-menu-wrapper';
import { OptionWrapper } from './components/option-wrapper';

const defaultProps = {
    isDisabled: false,
    isSearchable: true,
    onClose: null,
    options: [],
    placeholder: `${SELECTS_TEXTS.SEARCH}...`,
    title: '',
    dropdownMenu: { component: DropDown, props: {} },
    searchInput: { component: SelectorInputWithMagnifier, props: {} },
    optionComponent: { component: OptionWrapper, props: {} },
    singleValue: { component: SingleValueWithDropDown, props: {} },
    subMenu: { component: SubMenuWrapper, props: {} },
    wrapperStyles: null,
    components: {
        SubMenu: SubMenuWrapper,
    },
};

type Option ={
    checked: Boolean;
    label: string;
    value: string;
}

interface MultipleSelector{
    components:{
        SubMenu: React.ComponentType,
        props: Object,
    };
    dropdownMenu: { component: React.ComponentType, props: Object };
    searchInput: { component: React.ComponentType, props: Object };
    optionComponent: { component: React.ComponentType, props: Object };
    singleValue: { component: React.ComponentType, props: Object };
    isDisabled: Boolean;
    isSearchable: Boolean;
    onChange: Function;
    onClose: Function;
    placeholder: string;
    title: string;
    wrapperStyles:Object;
    options: Option[];
    dataAppcuesId?: string;
}

const MultipleSelector = ({
    dropdownMenu,
    isDisabled,
    isSearchable,
    onChange,
    onClose,
    optionComponent,
    options,
    placeholder,
    searchInput,
    singleValue,
    title,
    wrapperStyles,
    components,
    dataAppcuesId,
}:MultipleSelector) => {
    const [toggled, toggleValue, setToggled] = useToggle(false);
    const [filter, setFilter] = useState('');

    const handleOnCLick = () => {
        if (isDisabled) return;
        if (toggled && onClose) onClose();
        toggleValue();
    };

    const values = Object.values(options).filter(option => option.checked);
    const useOutsideHandler = (ref, handleVisibility, isVisible) => {
        const handleClickOutside = (event) => {
            if (!isVisible) return;

            const targetClicked = event.target;
            const shouldCallHandler = ref.current
            && !ref.current.contains(targetClicked);

            if (shouldCallHandler) {
                handleVisibility();
            }
        };

        useEffect(() => {
            document.addEventListener('mousedown', handleClickOutside);

            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        });
    };

    const wrapperRef = useRef(null);

    const closeMenu = () => {
        setToggled(false);
        setFilter('');
        if (onClose) onClose();
    };

    useOutsideHandler(wrapperRef, closeMenu, toggled);

    const onChangeSearchInput = (event) => {
        const { value } = event.target;
        setFilter(value);
    };

    const applyFilter = () => {
        if (filter && filter !== '') {
            return Object.values(options)
                .filter(option => option.label.toLowerCase()
                    .indexOf(filter.toLowerCase()) >= 0);
        }
        return options;
    };

    const { props: subMenuProps, SubMenu } = components;
    const { props: dropdownMenuProps, component: DropdownMenu } = dropdownMenu;
    const { props: singleValueProps, component: SingleValue } = singleValue;
    const { props: searchInputProps, component: SearchInput } = searchInput;
    const { props: optionProps, component: Option } = optionComponent;

    return (
        <DropDownWrapper wrapperStyles={wrapperStyles} data-appcues-id={dataAppcuesId}>
            <div
                ref={wrapperRef}
            >
                <SingleValue
                    data-testid={TEST_ID.MULTI_SELECTOR.SINGLE_VALUE}
                    isSelected
                    onClick={handleOnCLick}
                    values={values}
                    placeholder={placeholder}
                    {...singleValueProps}
                />
                {(
                    toggled && (
                        <DropdownMenu
                            title={title}
                            {...dropdownMenuProps}
                        >
                            {isSearchable && (
                                <SearchInput
                                    isDisabled={isDisabled}
                                    placeholder={placeholder}
                                    onChange={onChangeSearchInput}
                                    {...searchInputProps}
                                />
                            )}
                            <SubMenu
                                filter={filter}
                                options={applyFilter()}
                                onClick={onChange}
                                optionComponent={{ component: Option, props: optionProps }}
                                {...subMenuProps}
                            />
                        </DropdownMenu>
                    )
                )}
            </div>
        </DropDownWrapper>
    );
};

const DropDownWrapper = styled.div`
    position: relative;

    @media ${MobileScreen} {
        width: 100%;
    }
    ${({ wrapperStyles }) => wrapperStyles};
`;

MultipleSelector.defaultProps = defaultProps;

export default MultipleSelector;
